/**
 * Client-side filter for the collection page.
 *
 * Works WITHOUT Search & Discovery app. Reads filters from URL params,
 * hides/shows cards in the grid based on data-x-* attributes emitted by
 * snippets/product-card.liquid, and renders dynamic filter checkboxes
 * derived from the products actually on the page.
 *
 * URL param schema (matches Shopify's native shape so links work both ways):
 *   ?filter.p.vendor=Amouage&filter.p.vendor=Liis     (multi)
 *   ?filter.p.tag=note%3Aamber&filter.p.tag=gender%3Afeminine
 *   ?filter.p.price.gte=50&filter.p.price.lte=300
 *   ?filter.v.availability=in-stock
 *   ?sort_by=created-descending
 */
(function () {
  'use strict';

  // Run on collection pages only
  if (!document.querySelector('.x-plp')) return;

  var grid = document.querySelector('[data-x-plp-grid]') || document.querySelector('.x-plp__grid');
  if (!grid) return;

  var countEl = document.querySelector('[data-x-plp-count]');
  var filterForm = document.querySelector('[data-x-plp-filter-form]') || document.querySelector('#x-plp-filter-form');
  var filterDrawer = document.querySelector('[data-x-plp-filter-drawer]');
  var filterOpenBtn = document.querySelector('[data-x-plp-open-filters]');
  var activeCountEl = document.querySelector('[data-x-plp-active-count]');
  var drawerCountEl = document.querySelector('[data-x-plp-drawer-count]');

  // Model state — (re)built by boot() AFTER all pages are hydrated into the
  // grid, so client-side search/filter/chips cover the FULL catalog, not just
  // the first server-rendered page.
  var cards = [];
  var products = [];
  var facets = null;

  function formatMoney(cents) {
    var amount = (parseInt(cents || 0, 10) / 100).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return '$' + amount;
  }

  function variantPrice(variant) {
    return parseInt(variant && variant.price ? variant.price : 0, 10) || 0;
  }

  function isSampleVariant(variant) {
    var title = String((variant && (variant.public_title || variant.title || variant.name)) || '').toLowerCase();
    var compact = title.replace(/\s+/g, '').replace(/-/g, '');
    return title.indexOf('sample') !== -1 ||
      title.indexOf('discovery') !== -1 ||
      title.indexOf('vial') !== -1 ||
      title.indexOf('decant') !== -1 ||
      title.indexOf('travel') !== -1 ||
      title.indexOf('tester') !== -1 ||
      title.indexOf('mini') !== -1 ||
      compact.indexOf('0.7ml') !== -1 ||
      compact.indexOf('1ml') !== -1 ||
      compact.indexOf('1.5ml') !== -1 ||
      compact.indexOf('2ml') !== -1 ||
      compact.indexOf('3ml') !== -1 ||
      compact.indexOf('5ml') !== -1 ||
      compact.indexOf('10ml') !== -1 ||
      compact.indexOf('15ml') !== -1;
  }

  function lowestVariant(variants) {
    return (variants || []).reduce(function (best, variant) {
      if (!best) return variant;
      return variantPrice(variant) < variantPrice(best) ? variant : best;
    }, null);
  }

  function entryVariantFromMeta(productMeta) {
    if (!productMeta || !Array.isArray(productMeta.variants) || !productMeta.variants.length) return null;
    var bottles = productMeta.variants.filter(function (variant) { return !isSampleVariant(variant); });
    return lowestVariant(bottles.length ? bottles : productMeta.variants);
  }

  function entryVariantFromProduct(product) {
    if (!product || !Array.isArray(product.variants) || !product.variants.length) return null;
    var availableBottles = product.variants.filter(function (variant) {
      return variant.available && !isSampleVariant(variant);
    });
    if (availableBottles.length) return lowestVariant(availableBottles);
    var bottles = product.variants.filter(function (variant) { return !isSampleVariant(variant); });
    if (bottles.length) return lowestVariant(bottles);
    var available = product.variants.filter(function (variant) { return variant.available; });
    return lowestVariant(available.length ? available : product.variants);
  }

  function collectionMetaProducts() {
    return (window.meta && Array.isArray(window.meta.products)) ? window.meta.products : [];
  }

  function metaForCard(card) {
    var handle = card.getAttribute('data-product-handle');
    var id = card.getAttribute('data-product-id');
    return collectionMetaProducts().find(function (product) {
      return String(product.handle) === String(handle) || String(product.id) === String(id);
    });
  }

  function applyEntryVariantToCard(card, variant) {
    if (!card || !variant || !variant.price) return;
    var price = parseInt(variant.price, 10);
    if (!price) return;
    var dollars = price / 100;
    card.setAttribute('data-x-price', String(dollars));
    card.setAttribute('data-variant-id', variant.id);
    card.setAttribute('data-product-variant-id', variant.id);
    card.setAttribute('data-x-price-source', 'bottle');
    card.setAttribute('data-x-display-variant-title', variant.public_title || variant.title || variant.name || '');

    var priceRow = card.querySelector('.price-row');
    if (priceRow) {
      var from = priceRow.querySelector('.from');
      if (from) from.remove();
      var priceEl = priceRow.querySelector('.price') || priceRow.appendChild(document.createElement('span'));
      priceEl.className = 'price';
      priceEl.textContent = formatMoney(price);
    }

    card.querySelectorAll('[data-variant-id], [data-product-variant-id]').forEach(function (node) {
      node.setAttribute('data-variant-id', variant.id);
      node.setAttribute('data-product-variant-id', variant.id);
    });
    card.querySelectorAll('input[name="id"]').forEach(function (input) {
      input.value = variant.id;
    });
    card.querySelectorAll('[data-product-price]').forEach(function (node) {
      node.setAttribute('data-product-price', formatMoney(price));
    });
  }

  function correctEntryPrices(scope) {
    Array.from((scope || document).querySelectorAll('.product-card')).forEach(function (card) {
      var productMeta = metaForCard(card);
      var variant = entryVariantFromMeta(productMeta);
      if (variant) applyEntryVariantToCard(card, variant);
    });
  }

  function hydrateEntryPricesFromProducts(scope, maxCards) {
    Array.from((scope || document).querySelectorAll('.product-card'))
      .filter(function (card) { return card.getAttribute('data-x-price-source') !== 'bottle'; })
      .slice(0, maxCards || 32)
      .forEach(function (card) {
        var productUrl = card.getAttribute('data-product-url');
        if (!productUrl) return;
        var jsonUrl = productUrl.split('?')[0].replace(/\/$/, '') + '.js';
        fetch(jsonUrl, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
          .then(function (response) { return response.ok ? response.json() : null; })
          .then(function (product) {
            var variant = entryVariantFromProduct(product);
            if (variant) applyEntryVariantToCard(card, variant);
          })
          .catch(function () {});
      });
  }

  // ─── Parse cards into a lightweight model ───────────────────────────
  function parseCard(card) {
    var tags = (card.getAttribute('data-x-tags') || '')
      .split(',').map(function (t) { return t.trim().toLowerCase(); }).filter(Boolean);
    return {
      el: card,
      vendor: (card.getAttribute('data-x-vendor') || '').toLowerCase(),
      tags: tags,
      price: parseFloat(card.getAttribute('data-x-price') || '0'),
      available: card.getAttribute('data-x-available') === 'true',
      created: parseInt(card.getAttribute('data-x-created') || '0', 10),
    };
  }
  // ─── Derive filter groups from products ─────────────────────────────
  function derive() {
    var vendors = {};
    var notes = {};
    var genders = {};
    var minPrice = Infinity, maxPrice = 0;
    products.forEach(function (p) {
      if (p.vendor) vendors[p.vendor] = (vendors[p.vendor] || 0) + 1;
      // Product cards intentionally expose the lowest/sample entry price, so
      // the derived range should match what shoppers see on the shelf.
      if (p.price > 0) {
        if (p.price < minPrice) minPrice = p.price;
        if (p.price > maxPrice) maxPrice = p.price;
      }
      p.tags.forEach(function (t) {
        if (t.indexOf('note:') === 0) {
          var n = t.slice(5);
          notes[n] = (notes[n] || 0) + 1;
        }
        if (t.indexOf('gender:') === 0) {
          var g = t.slice(7);
          genders[g] = (genders[g] || 0) + 1;
        }
      });
    });
    return {
      vendors: vendors, notes: notes, genders: genders,
      minPrice: isFinite(minPrice) ? Math.floor(minPrice) : 0,
      maxPrice: maxPrice ? Math.ceil(maxPrice) : 500,
    };
  }

  // ─── URL state helpers ──────────────────────────────────────────────
  function readUrl() {
    var p = new URLSearchParams(window.location.search);
    return {
      vendors: p.getAll('filter.p.vendor').map(function (s) { return s.toLowerCase(); }),
      tags: p.getAll('filter.p.tag').map(function (s) { return decodeURIComponent(s).toLowerCase(); }),
      genders: p.getAll('filter.p.tag')
        .map(function (s) { return decodeURIComponent(s).toLowerCase(); })
        .filter(function (s) { return s.indexOf('gender:') === 0; })
        .map(function (s) { return s.slice(7); }),
      notes: p.getAll('filter.p.tag')
        .map(function (s) { return decodeURIComponent(s).toLowerCase(); })
        .filter(function (s) { return s.indexOf('note:') === 0; })
        .map(function (s) { return s.slice(5); }),
      priceGte: parseFloat(p.get('filter.p.price.gte') || ''),
      priceLte: parseFloat(p.get('filter.p.price.lte') || ''),
      availability: p.get('filter.v.availability') || null,
      sort: p.get('sort_by') || null,
    };
  }

  function setUrl(updates) {
    var p = new URLSearchParams(window.location.search);
    Object.keys(updates).forEach(function (key) {
      var val = updates[key];
      p.delete(key);
      if (Array.isArray(val)) val.forEach(function (v) { p.append(key, v); });
      else if (val !== null && val !== undefined && val !== '') p.set(key, val);
    });
    var qs = p.toString();
    var newUrl = window.location.pathname + (qs ? '?' + qs : '');
    window.history.replaceState(null, '', newUrl);
  }

  // Live text search — typing in the [data-x-plp-search] input narrows by
  // product title / vendor / note tags in real time.
  var searchQuery = '';
  function parseCardText(card) {
    var bits = [];
    var title = card.querySelector('.product-card__title, .product-card__name');
    var brand = card.querySelector('.product-card__brand, .product-card__vendor');
    if (title) bits.push(title.textContent);
    if (brand) bits.push(brand.textContent);
    bits.push(card.getAttribute('data-x-vendor') || '');
    bits.push(card.getAttribute('data-x-tags') || '');
    return bits.join(' ').toLowerCase();
  }
  // ─── Apply filters ──────────────────────────────────────────────────
  function applyFilters() {
    var state = readUrl();
    var shown = 0;
    var q = (searchQuery || '').trim().toLowerCase();
    products.forEach(function (p) {
      var ok = true;
      if (state.vendors.length && state.vendors.indexOf(p.vendor) === -1) ok = false;
      if (ok && state.genders.length) {
        var hasGender = state.genders.some(function (g) { return p.tags.indexOf('gender:' + g) !== -1; });
        if (!hasGender) ok = false;
      }
      if (ok && state.notes.length) {
        var hasNote = state.notes.some(function (n) { return p.tags.indexOf('note:' + n) !== -1; });
        if (!hasNote) ok = false;
      }
      if (ok && !isNaN(state.priceGte) && p.price < state.priceGte) ok = false;
      if (ok && !isNaN(state.priceLte) && p.price > state.priceLte) ok = false;
      if (ok && state.availability === 'in-stock' && !p.available) ok = false;
      if (ok && state.availability === 'out-of-stock' && p.available) ok = false;
      // Live search across title + brand + tags
      if (ok && q) {
        var matched = q.split(/\s+/).every(function (tok) {
          return tok === '' || p.searchText.indexOf(tok) !== -1;
        });
        if (!matched) ok = false;
      }
      p.el.style.display = ok ? '' : 'none';
      if (ok) shown++;
    });
    if (countEl) countEl.textContent = shown;
    syncMobileChrome(state, shown);
    // Apply sort
    if (state.sort) applySort(state.sort);
    // Re-render chips + brand bar
    renderChips(state);
    renderBrandBar(state);
    // #63 — annotate each visible card with the active filters it matched
    renderMatchTags(state);
  }

  function isMobileFilters() {
    return window.matchMedia && window.matchMedia('(max-width: 960px)').matches;
  }

  function isPriceActive(state) {
    return !isNaN(state.priceGte) || !isNaN(state.priceLte);
  }

  function activeFilterCount(state) {
    return state.vendors.length +
      state.genders.length +
      state.notes.length +
      (state.availability ? 1 : 0) +
      (isPriceActive(state) ? 1 : 0);
  }

  function groupOpenAttr(isActive, desktopDefaultOpen) {
    if (isActive || (!isMobileFilters() && desktopDefaultOpen)) return ' open';
    return '';
  }

  function syncMobileChrome(state, shown) {
    var count = activeFilterCount(state);
    if (activeCountEl) {
      activeCountEl.textContent = count;
      activeCountEl.hidden = !count;
    }
    if (drawerCountEl) drawerCountEl.textContent = shown;
  }

  function openFilters() {
    document.body.classList.add('x-plp-filters-open');
    if (filterDrawer) filterDrawer.setAttribute('aria-modal', 'true');
    if (filterOpenBtn) filterOpenBtn.setAttribute('aria-expanded', 'true');
  }

  function closeFilters() {
    document.body.classList.remove('x-plp-filters-open');
    if (filterDrawer) filterDrawer.removeAttribute('aria-modal');
    if (filterOpenBtn) filterOpenBtn.setAttribute('aria-expanded', 'false');
  }

  // ─── #63: note tags on each card ────────────────────────────────────
  // When any filter is active, show a small translucent line of the product's
  // NOTES between the title and price (never the brand — the card already
  // shows the vendor as its kicker, so echoing it read as "brand twice").
  // Actively-filtered notes are listed first.
  function renderMatchTags(state) {
    var anyActive = state.vendors.length || state.genders.length || state.notes.length;
    products.forEach(function (p) {
      var meta = p.el.querySelector('.product-meta');
      if (!meta) return;
      var el = meta.querySelector('.product-card__match');
      if (!anyActive || p.el.style.display === 'none') { if (el) el.remove(); return; }
      var own = p.tags
        .filter(function (t) { return t.indexOf('note:') === 0; })
        .map(function (t) { return t.slice(5); });
      if (!own.length) { if (el) el.remove(); return; }
      // matched notes first, then the rest of the product's notes
      var ordered = state.notes.filter(function (n) { return own.indexOf(n) !== -1; })
        .concat(own.filter(function (n) { return state.notes.indexOf(n) === -1; }));
      var labels = ordered.slice(0, 4).map(cap);
      if (!el) {
        el = document.createElement('div');
        el.className = 'product-card__match';
        meta.insertBefore(el, meta.querySelector('.price-row'));
      }
      el.textContent = labels.join(' · ');
    });
  }

  function renderBrandBar(state) {
    var host = document.querySelector('[data-x-plp-brandbar]');
    if (!host) return;
    var vendors = Object.keys(facets.vendors).sort();
    if (!vendors.length) { host.innerHTML = ''; return; }
    var html = vendors.map(function (v) {
      var on = state.vendors.indexOf(v) !== -1;
      return '<button type="button" class="x-plp__brand-chip ' + (on ? 'is-active' : '') + '" data-x-brand-toggle="' + v + '">' +
        titleCase(v) +
        '<span class="x-plp__brand-chip-count">' + facets.vendors[v] + '</span>' +
      '</button>';
    }).join('');
    host.innerHTML = html;
  }

  function applySort(sortBy) {
    var visible = products.filter(function (p) { return p.el.style.display !== 'none'; });
    var sorters = {
      'best-selling':       function (a, b) { return b.created - a.created; }, // fallback to newest
      'created-descending': function (a, b) { return b.created - a.created; },
      'created-ascending':  function (a, b) { return a.created - b.created; },
      'price-ascending':    function (a, b) { return a.price - b.price; },
      'price-descending':   function (a, b) { return b.price - a.price; },
      'title-ascending':    function (a, b) { return (a.el.querySelector('.name') || {textContent: ''}).textContent.localeCompare((b.el.querySelector('.name') || {textContent: ''}).textContent); },
      'manual':             function () { return 0; },
    };
    var fn = sorters[sortBy] || sorters['created-descending'];
    visible.sort(fn).forEach(function (p) { grid.appendChild(p.el); });
  }

  // ─── Render filter sidebar from derived facets ──────────────────────
  function renderSidebar() {
    if (!filterForm) return;
    var state = readUrl();
    var html = '';

    // Brand — same filter.p.vendor state as the House Index rail.
    var vKeys = Object.keys(facets.vendors).sort();
    if (vKeys.length > 0) {
      html += '<details class="x-plp__filter-group"' + groupOpenAttr(state.vendors.length > 0, true) + '><summary class="x-plp__filter-title">Brand</summary><div class="x-plp__filter-rows">';
      vKeys.forEach(function (v) {
        var on = state.vendors.indexOf(v) !== -1;
        html += '<label class="x-plp__filter-row ' + (on ? 'is-active' : '') + '">' +
          '<input type="checkbox" data-x-filter="vendor" value="' + escapeAttr(v) + '" ' + (on ? 'checked' : '') + '>' +
          '<span class="x-plp__filter-label">' + escapeHtml(titleCase(v)) + '</span>' +
          '<span class="x-plp__filter-count">' + facets.vendors[v] + '</span>' +
          '</label>';
      });
      html += '</div></details>';
    }

    // Gender (always show — pinned top)
    var gKeys = Object.keys(facets.genders).sort();
    if (gKeys.length > 0) {
      html += '<details class="x-plp__filter-group" open><summary class="x-plp__filter-title">Gender</summary><div class="x-plp__filter-rows">';
      gKeys.forEach(function (g) {
        var on = state.genders.indexOf(g) !== -1;
        html += '<label class="x-plp__filter-row ' + (on ? 'is-active' : '') + '">' +
          '<input type="checkbox" data-x-filter="gender" value="' + g + '" ' + (on ? 'checked' : '') + '>' +
          '<span class="x-plp__filter-label">' + cap(g) + '</span>' +
          '<span class="x-plp__filter-count">' + facets.genders[g] + '</span>' +
          '</label>';
      });
      html += '</div></details>';
    } else {
      // Static fallback if no gender tags exist yet
      html += '<details class="x-plp__filter-group" open><summary class="x-plp__filter-title">Gender</summary><div class="x-plp__filter-rows">' +
        ['feminine','masculine','unisex'].map(function (g) {
          var on = state.genders.indexOf(g) !== -1;
          return '<label class="x-plp__filter-row ' + (on ? 'is-active' : '') + '">' +
            '<input type="checkbox" data-x-filter="gender" value="' + g + '" ' + (on ? 'checked' : '') + '>' +
            '<span class="x-plp__filter-label">' + cap(g) + '</span>' +
            '</label>';
        }).join('') +
        '</div></details>';
    }

    // Notes — open by default.
    var nKeys = Object.keys(facets.notes).sort();
    if (nKeys.length > 0) {
      html += '<details class="x-plp__filter-group"' + groupOpenAttr(state.notes.length > 0, true) + '><summary class="x-plp__filter-title">Notes</summary><div class="x-plp__filter-rows">';
      nKeys.forEach(function (n) {
        var on = state.notes.indexOf(n) !== -1;
        html += '<label class="x-plp__filter-row ' + (on ? 'is-active' : '') + '">' +
          '<input type="checkbox" data-x-filter="note" value="' + n + '" ' + (on ? 'checked' : '') + '>' +
          '<span class="x-plp__filter-label">' + cap(n) + '</span>' +
          '<span class="x-plp__filter-count">' + facets.notes[n] + '</span>' +
          '</label>';
      });
      html += '</div></details>';
    }

    // Availability — below Notes, above Price.
    html += '<details class="x-plp__filter-group"' + groupOpenAttr(!!state.availability, true) + '><summary class="x-plp__filter-title">Availability</summary><div class="x-plp__filter-rows">' +
      '<label class="x-plp__filter-row ' + (state.availability === 'in-stock' ? 'is-active' : '') + '">' +
        '<input type="radio" name="x-availability" data-x-filter="availability" value="in-stock" ' + (state.availability === 'in-stock' ? 'checked' : '') + '>' +
        '<span class="x-plp__filter-label">In stock</span>' +
      '</label>' +
      '<label class="x-plp__filter-row ' + (state.availability === 'out-of-stock' ? 'is-active' : '') + '">' +
        '<input type="radio" name="x-availability" data-x-filter="availability" value="out-of-stock" ' + (state.availability === 'out-of-stock' ? 'checked' : '') + '>' +
        '<span class="x-plp__filter-label">Out of stock</span>' +
      '</label>' +
      (state.availability ? '<button type="button" class="x-plp__filter-clear" data-x-clear="availability">Clear</button>' : '') +
      '</div></details>';

    // Price — kept at the bottom of the filter panel.
    html += '<details class="x-plp__filter-group"' + groupOpenAttr(isPriceActive(state), true) + '><summary class="x-plp__filter-title">Price</summary><div class="x-plp__filter-rows">' +
      '<div class="x-plp__price">' +
        '<div class="x-plp__price-inputs">' +
          '<input class="x-plp__price-input" type="number" placeholder="Min" min="0" value="' + (isNaN(state.priceGte) ? '' : state.priceGte) + '" data-x-filter="priceGte">' +
          '<span class="x-plp__price-sep">to</span>' +
          '<input class="x-plp__price-input" type="number" placeholder="Max" min="0" value="' + (isNaN(state.priceLte) ? '' : state.priceLte) + '" data-x-filter="priceLte">' +
        '</div>' +
        '<button type="button" class="x-plp__price-apply" data-x-apply-price>Apply</button>' +
        '<div class="x-plp__price-range">$' + facets.minPrice + ' – $' + facets.maxPrice + '</div>' +
      '</div></div></details>';

    // Clear all
    var anyActive = state.vendors.length || state.tags.length || state.availability || !isNaN(state.priceGte) || !isNaN(state.priceLte);
    if (anyActive) {
      html += '<button type="button" class="x-plp__filter-clear" data-x-clear-all>Clear all filters</button>';
    }

    filterForm.innerHTML = html;
  }

  function renderChips(state) {
    var chipsHost = document.querySelector('[data-x-plp-chips]');
    if (!chipsHost) return;
    var html = '';
    state.vendors.forEach(function (v) {
      html += '<button type="button" class="x-plp__chip" data-x-chip-remove="vendor" data-x-chip-value="' + v + '">' + titleCase(v) + ' ×</button>';
    });
    state.genders.forEach(function (g) {
      html += '<button type="button" class="x-plp__chip" data-x-chip-remove="gender" data-x-chip-value="' + g + '">' + cap(g) + ' ×</button>';
    });
    state.notes.forEach(function (n) {
      html += '<button type="button" class="x-plp__chip" data-x-chip-remove="note" data-x-chip-value="' + n + '">' + cap(n) + ' ×</button>';
    });
    if (!isNaN(state.priceGte) || !isNaN(state.priceLte)) {
      html += '<button type="button" class="x-plp__chip" data-x-chip-remove="price">$' +
        (isNaN(state.priceGte) ? facets.minPrice : state.priceGte) + ' – $' +
        (isNaN(state.priceLte) ? facets.maxPrice : state.priceLte) + ' ×</button>';
    }
    if (state.availability) {
      html += '<button type="button" class="x-plp__chip" data-x-chip-remove="availability">' +
        (state.availability === 'in-stock' ? 'In stock' : 'Out of stock') + ' ×</button>';
    }
    chipsHost.innerHTML = html;
  }

  // ─── Event handlers ─────────────────────────────────────────────────
  function onChange(e) {
    var input = e.target.closest('[data-x-filter]');
    if (!input) return;
    var type = input.getAttribute('data-x-filter');
    var val = input.value.toLowerCase();
    var state = readUrl();

    if (type === 'gender') {
      var arr = state.genders.slice();
      var i = arr.indexOf(val);
      if (input.checked) { if (i === -1) arr.push(val); }
      else { if (i !== -1) arr.splice(i, 1); }
      var notesTags = state.notes.map(function (n) { return 'note:' + n; });
      var genderTags = arr.map(function (g) { return 'gender:' + g; });
      setUrl({ 'filter.p.tag': genderTags.concat(notesTags) });
    } else if (type === 'note') {
      var arr = state.notes.slice();
      var i = arr.indexOf(val);
      if (input.checked) { if (i === -1) arr.push(val); }
      else { if (i !== -1) arr.splice(i, 1); }
      var noteTags = arr.map(function (n) { return 'note:' + n; });
      var genderTags = state.genders.map(function (g) { return 'gender:' + g; });
      setUrl({ 'filter.p.tag': genderTags.concat(noteTags) });
    } else if (type === 'vendor') {
      var arr = state.vendors.slice();
      var i = arr.indexOf(val);
      if (input.checked) { if (i === -1) arr.push(val); }
      else { if (i !== -1) arr.splice(i, 1); }
      setUrl({ 'filter.p.vendor': arr });
    } else if (type === 'availability') {
      setUrl({ 'filter.v.availability': input.checked ? val : '' });
    } else if (type === 'priceGte' || type === 'priceLte') {
      return; // applied on button click
    }
    applyFilters();
    renderSidebar(); // re-render to update counts/active state
  }

  function onClick(e) {
    if (e.target.closest('[data-x-plp-open-filters]')) {
      e.preventDefault();
      openFilters();
      return;
    }
    if (e.target.closest('[data-x-plp-close-filters]')) {
      e.preventDefault();
      e.stopPropagation();
      closeFilters();
      return;
    }
    var brandToggle = e.target.closest('[data-x-brand-toggle]');
    if (brandToggle) {
      var vendor = (brandToggle.getAttribute('data-x-brand-toggle') || '').toLowerCase();
      var state = readUrl();
      var arr = state.vendors.slice();
      var idx = arr.indexOf(vendor);
      if (idx >= 0) arr.splice(idx, 1);
      else arr.push(vendor);
      setUrl({ 'filter.p.vendor': arr });
      applyFilters();
      renderSidebar();
      return;
    }
    if (e.target.closest('[data-x-apply-price]')) {
      var gte = document.querySelector('[data-x-filter="priceGte"]');
      var lte = document.querySelector('[data-x-filter="priceLte"]');
      setUrl({
        'filter.p.price.gte': gte && gte.value ? gte.value : '',
        'filter.p.price.lte': lte && lte.value ? lte.value : '',
      });
      applyFilters();
      renderSidebar();
      return;
    }
    var chip = e.target.closest('[data-x-chip-remove]');
    if (chip) {
      var type = chip.getAttribute('data-x-chip-remove');
      var val = (chip.getAttribute('data-x-chip-value') || '').toLowerCase();
      var state = readUrl();
      if (type === 'vendor') setUrl({ 'filter.p.vendor': state.vendors.filter(function (v) { return v !== val; }) });
      else if (type === 'gender') {
        var g = state.genders.filter(function (x) { return x !== val; });
        setUrl({ 'filter.p.tag': g.map(function (x) { return 'gender:' + x; }).concat(state.notes.map(function (x) { return 'note:' + x; })) });
      }
      else if (type === 'note') {
        var n = state.notes.filter(function (x) { return x !== val; });
        setUrl({ 'filter.p.tag': state.genders.map(function (x) { return 'gender:' + x; }).concat(n.map(function (x) { return 'note:' + x; })) });
      }
      else if (type === 'price') setUrl({ 'filter.p.price.gte': '', 'filter.p.price.lte': '' });
      else if (type === 'availability') setUrl({ 'filter.v.availability': '' });
      applyFilters();
      renderSidebar();
      return;
    }
    if (e.target.closest('[data-x-clear-all]') || e.target.closest('[data-x-clear="availability"]')) {
      window.history.replaceState(null, '', window.location.pathname);
      applyFilters();
      renderSidebar();
      return;
    }
  }

  function cap(s) { return s.charAt(0).toUpperCase() + s.slice(1); }
  function titleCase(s) { return s.replace(/\b\w/g, function (c) { return c.toUpperCase(); }); }
  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }
  function escapeAttr(s) { return escapeHtml(s); }

  // Wire up
  document.addEventListener('change', onChange);
  document.addEventListener('click', onClick);
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeFilters();
  });

  // Live search — debounced text input across product title/brand/tags
  var searchInput = document.querySelector('[data-x-plp-search]');
  if (searchInput) {
    var searchT = null;
    searchInput.addEventListener('input', function (e) {
      clearTimeout(searchT);
      searchT = setTimeout(function () {
        searchQuery = e.target.value || '';
        applyFilters();
      }, 120);
    });
    searchInput.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { e.target.value = ''; searchQuery = ''; applyFilters(); }
    });
  }

  // ─── Boot: build the model from every card now in the grid, then render ─
  function boot() {
    correctEntryPrices(grid);
    hydrateEntryPricesFromProducts(grid, 80);
    cards = Array.from(grid.querySelectorAll('.product-card'));
    products = cards.map(parseCard);
    products.forEach(function (p) { p.searchText = parseCardText(p.el); });
    facets = derive();
    renderSidebar();
    applyFilters();
  }

  // ─── Hydrate every page into the grid before booting ─────────────────
  // The grid is server-paginated (Shopify caps a page at 50 products) but all
  // filtering/search runs client-side over the DOM. Without this, search and
  // the brand chips only ever see the first page. Fetch the remaining pages,
  // append their cards, then build the model over the full catalog.
  function hydrateAll(done) {
    var total = parseInt(grid.getAttribute('data-x-plp-total') || '0', 10);
    var have = grid.querySelectorAll('.product-card').length;
    if (!total || have >= total) { done(); return; }
    var path = window.location.pathname;
    var page = 2;
    function fetchNext() {
      var params = new URLSearchParams(window.location.search);
      params.set('page', page);
      fetch(path + '?' + params.toString())
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (html) {
          if (!html) { done(); return; }
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var more = doc.querySelectorAll('[data-x-plp-grid] .product-card, .x-plp__grid .product-card');
          if (!more.length) { done(); return; }
          more.forEach(function (c) { grid.appendChild(document.importNode(c, true)); });
          page++;
          if (grid.querySelectorAll('.product-card').length >= total || page > 25) done();
          else fetchNext();
        })
        .catch(function () { done(); });
    }
    fetchNext();
  }

  // Hide the server pagination nav — client-side now owns the full list.
  var pager = document.querySelector('.x-plp__pagination');
  if (pager) pager.style.display = 'none';

  // Correct the first visible page immediately; full-catalog hydration can take
  // a moment, and prices should not flash full-bottle values while it runs.
  correctEntryPrices(grid);
  hydrateEntryPricesFromProducts(grid, 50);
  hydrateAll(boot);
})();
